import React, { useState, useEffect } from "react";
import {
  MessageCircle,
  Phone,
  Mail,
  Send,
  Bot,
  Clock,
  HelpCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getDeliverySettings,
  DeliverySettings,
  getLocationSettings,
} from "../api/settings";
import { LocationSettings } from "../types";
import { toast } from "sonner";
import { MAX_SPEED, MIN_SPEED, MONEY_DEVISE } from "../utils/utils";

const CustomerService: React.FC = () => {
  const { t } = useTranslation();
  const [deliverySettings, setDeliverySettings] =
    useState<DeliverySettings | null>(null);
  const [locationSettings, setLocationSettings] =
    useState<LocationSettings | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data, error } = await getDeliverySettings();
      if (data) {
        setDeliverySettings(data);
      } else {
        console.error("Failed to fetch delivery settings:", error);
      }
    };

    const fetchLocationSettings = async () => {
      const { data, error } = await getLocationSettings();
      console.log("fetchLocationSettings", data);
      if (data) {
        setLocationSettings(data);
      } else {
        console.error("Failed to fetch delivery settings:", error);
        toast.error(error?.message);
      }
    };

    fetchSettings();
    fetchLocationSettings();
  }, []);
  const [message, setMessage] = useState("");
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      sender: "bot",
      content: t("customerService.assistantWelcome"),
      timestamp: new Date(),
    },
  ]);

  const quickQuestions = [
    t("customerService.whereIsMyOrder"),
    t("customerService.modifyMyOrder"),
    t("customerService.paymentProblem"),
    t("customerService.changeAddress"),
  ];

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    const newMessage = {
      id: chatMessages.length + 1,
      sender: "user" as const,
      content: message,
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, newMessage]);
    setMessage("");

    // Simulate bot response
    setTimeout(() => {
      const responses = [
        t("customerService.defaultResponse1"),
        t("customerService.defaultResponse2"),
        t("customerService.defaultResponse3"),
        t("customerService.defaultResponse4"),
      ];

      const randomResponse =
        responses[Math.floor(Math.random() * responses.length)];

      const botResponse = {
        id: chatMessages.length + 2,
        sender: "bot" as const,
        content: randomResponse,
        timestamp: new Date(),
      };
      setChatMessages((prev) => [...prev, botResponse]);
    }, 1000);
  };

  const handleQuickQuestion = (question: string) => {
    const newMessage = {
      id: chatMessages.length + 1,
      sender: "user" as const,
      content: question,
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, newMessage]);

    // Simulate specific responses
    setTimeout(() => {
      let response = "";
      switch (question) {
        case t("customerService.whereIsMyOrder"):
          response = t("customerService.whereIsMyOrderResponse");
          break;
        case t("customerService.modifyMyOrder"):
          response = t("customerService.modifyMyOrderResponse");
          break;
        case t("customerService.paymentProblem"):
          response = t("customerService.paymentProblemResponse");
          break;
        case t("customerService.changeAddress"):
          response = t("customerService.changeAddressResponse");
          break;
        default:
          response = t("customerService.defaultQuestionResponse");
      }

      const botResponse = {
        id: chatMessages.length + 2,
        sender: "bot" as const,
        content: response,
        timestamp: new Date(),
      };
      setChatMessages((prev) => [...prev, botResponse]);
    }, 1500);
  };

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/+15815805573?text=${t("customerService.needHelp")}`,
      "_blank"
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-black mb-2">
          {t("customerService.customerService")}
        </h1>
        <p className="text-gray-600">{t("customerService.weAreHereToHelp")}</p>
      </div>

      <div className="flex justify-center">
        {/* Contact Methods */}
        <div className="space-y-6 lg:w-1/3">
          {/* FAQ */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
              <HelpCircle className="w-5 h-5 mr-2 text-orange-500" />
              {t("customerService.faq")}
            </h3>
            <div className="space-y-3 text-sm">
              <details className="group">
                <summary className="cursor-pointer text-orange-500 hover:text-orange-600 font-medium">
                  {t("customerService.canIModify")}
                </summary>
                <p className="mt-2 text-gray-600 text-xs leading-relaxed">
                  {t("customerService.canIModifyAnswer")}
                </p>
              </details>

              <details className="group">
                <summary className="cursor-pointer text-orange-500 hover:text-orange-600 font-medium">
                  {t("customerService.deliveryFees")}
                </summary>
                <p className="mt-2 text-gray-600 text-xs leading-relaxed">
                  {t("customerService.deliveryFeesAnswer")}
                  {` ${deliverySettings?.pricePerKm} ${MONEY_DEVISE}/Km`}
                </p>
              </details>

              <details className="group">
                <summary className="cursor-pointer text-orange-500 hover:text-orange-600 font-medium">
                  {t("customerService.deliveryTime")}
                </summary>
                <p className="mt-2 text-gray-600 text-xs leading-relaxed">
                  {`${((0 / MAX_SPEED) * 60).toFixed(0)} - ${(
                    ((deliverySettings?.maxDeliveryDistanceKm || 0) /
                      MIN_SPEED) *
                    60
                  ).toFixed(0)}`}{" "}
                  {t("customerService.deliveryTimeAnswer")}
                </p>
              </details>
            </div>
          </div>

          {/* Contact Cards */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
              <MessageCircle className="w-5 h-5 mr-2 text-orange-500" />
              {t("customerService.contactUs")}
            </h3>

            <div className="space-y-3">
              <button
                onClick={handleWhatsApp}
                className="w-full flex items-center justify-center space-x-3 p-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="font-medium">
                  {t("customerService.whatsapp")}
                </span>
              </button>

              <a
                href="tel:+1 (581) 580-5573"
                className="w-full flex items-center justify-center space-x-3 p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Phone className="w-4 h-4" />
                <span className="font-medium">
                  {t("customerService.phone")}
                </span>
              </a>

              <a
                href="mailto:managerfood25@gmail.com"
                className="w-full flex items-center justify-center space-x-3 p-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Mail className="w-4 h-4" />
                <span className="font-medium">
                  {t("customerService.email")}
                </span>
              </a>
            </div>
          </div>

          {/* Hours */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-orange-500" />
              {t("customerService.hours")}
            </h3>
            <div className="space-y-2 text-sm">
              {deliverySettings ? (
                deliverySettings.deliverySlots
                  .filter((slot) => slot.isActive)
                  .sort(
                    (a, b) =>
                      [
                        "MONDAY",
                        "TUESDAY",
                        "WEDNESDAY",
                        "THURSDAY",
                        "FRIDAY",
                        "SATURDAY",
                        "SUNDAY",
                      ].indexOf(a.dayOfWeek) -
                      [
                        "MONDAY",
                        "TUESDAY",
                        "WEDNESDAY",
                        "THURSDAY",
                        "FRIDAY",
                        "SATURDAY",
                        "SUNDAY",
                      ].indexOf(b.dayOfWeek)
                  )
                  .map((slot) => (
                    <div key={slot.id} className="flex justify-between">
                      <span className="text-gray-600 capitalize">
                        {t(
                          `weekdays.${slot.dayOfWeek.toLowerCase()}`,
                          slot.dayOfWeek.toLowerCase()
                        )}
                      </span>
                      <span className="font-medium text-black">
                        {slot.pickupSchedule.startTime.slice(0, 5)} -{" "}
                        {slot.pickupSchedule.endTime.slice(0, 5)}
                      </span>
                    </div>
                  ))
              ) : (
                <p>{t("cart.loadingHours", "Loading hours...")}</p>
              )}
              <div className="mt-3 p-2 bg-green-50 rounded-lg">
                <div className="flex items-center text-green-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-xs font-medium">
                    {t("customerService.online")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Live Chat */}
        {/* <div className="lg:col-span-3">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            
            <div className="p-4 bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center shadow-sm">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-black">{t('customerService.assistant')}</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <p className="text-sm text-green-600 font-medium">{t('customerService.onlineStatus')}</p>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {t('customerService.avgResponse')}
                </div>
              </div>
            </div>

            
            <div className="p-4 bg-gray-50 border-b border-gray-100">
              <p className="text-sm text-gray-600 mb-3">{t('customerService.faqTitle')}</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickQuestion(question)}
                    className="px-3 py-1.5 bg-white text-gray-700 rounded-full text-xs border border-gray-200 hover:border-orange-300 hover:text-orange-600 transition-all duration-200"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            
            <div className="h-96 p-4 overflow-y-auto space-y-4 bg-gray-50">
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="max-w-xs lg:max-w-md">
                    <div
                      className={`px-4 py-3 rounded-2xl shadow-sm ${
                        msg.sender === 'user'
                          ? 'bg-orange-500 text-white rounded-br-md'
                          : 'bg-white text-gray-900 border border-gray-100 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm leading-relaxed">{msg.content}</p>
                    </div>
                    <p className={`text-xs mt-1 px-2 ${
                      msg.sender === 'user' ? 'text-right text-orange-200' : 'text-left text-gray-400'
                    }`}>
                      {msg.timestamp.toLocaleTimeString('fr-FR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            
            <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-gray-100">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('customerService.typeYourMessage')}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus-ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                />
                <button
                  type="submit"
                  disabled={!message.trim()}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md ${
                    message.trim()
                      ? 'bg-orange-500 text-white hover:bg-orange-600'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('customerService.enterToSend')}
              </p>
            </form>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default CustomerService;
